import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import api from "api/axios";
import { StageBadge, DeadlineChip } from "features/crm/ui/crmUi";

interface RadarItem {
  id: number;
  title: string;
  stage_key: string | null;
  stage_name: string | null;
  agency_name: string | null;
  solicitation_number: string | null;
  questions_deadline: string | null;
  proposal_deadline: string | null;
  next_deadline: string;
}

export default function DeadlineRadar() {
  const [items, setItems] = useState<RadarItem[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/admin/crm/deadline-radar", { params: { days: 14 } })
      .then((res) => {
        setItems(res.data);
        setError(false);
      })
      .catch(() => {
        setItems([]);
        setError(true);
      });
  }, []);

  if (error) {
    return (
      <p className="text-xs text-muted-foreground font-inter">
        Couldn't load the RFP deadline radar — try refreshing.
      </p>
    );
  }

  // Nothing due soon — stay out of the way.
  if (!items || items.length === 0) return null;

  return (
    <div>
      <h2 className="text-base font-bold text-foreground font-inter mb-3 flex items-center gap-2">
        <CalendarClock className="w-4 h-4 text-warning" />
        RFP Deadline Radar — next 14 days
      </h2>
      <div className="rounded-2xl border border-border bg-surface divide-y divide-border overflow-hidden">
        {items.map((item) => (
          <Link
            key={item.id}
            to="/admin/crm"
            className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-surface-muted transition-colors"
          >
            <div className="min-w-0">
              <p className="text-sm text-foreground font-inter truncate">{item.title}</p>
              <p className="text-xs text-muted-foreground font-inter truncate">
                {[item.agency_name, item.solicitation_number].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0 text-sm">
              {item.stage_key && item.stage_name && (
                <StageBadge stageKey={item.stage_key} stageName={item.stage_name} />
              )}
              <div className="text-right text-xs text-muted-foreground font-inter space-y-0.5">
                {item.questions_deadline && (
                  <div>Questions: <DeadlineChip value={item.questions_deadline} /></div>
                )}
                {item.proposal_deadline && (
                  <div>Proposal: <DeadlineChip value={item.proposal_deadline} /></div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
